import React, { useMemo, useState } from "react";

import { ConcernCatalogDrawer } from "./ConcernCatalogDrawer";
import { FoodEditor } from "./FoodEditor";
import { WebLookup } from "./WebLookup";
import {
  BLOCKING_CONCERN_CODES,
  blockingFieldTargets,
  describeConcern,
  explainConcern,
} from "./concerns";
import {
  defaultPortionIndex,
  formatAmount,
  formatGrams,
  getEffectiveQuantity,
  getTotalGrams,
  prettyNutrientName,
  scaleNutrition,
} from "./nutrition";
import { Badge, Spinner } from "./shared";
import { ConcernDetail, PassioFood, PassioPortion } from "./types";

/**
 * Reusable nutrition-facts widget for a Passio food record — the single place
 * we show (and optionally edit) a food across the Catalog Manager.
 *
 * View mode handles per-100g -> portion scaling (nutrients are stored per 100g)
 * and renders the portion picker, scaled macro summary, the full nutrient list,
 * branded/product info, ingredients, tags, concerns, quality and sources.
 *
 * Edit mode (when `onChange` is provided and the user clicks "Edit") swaps the
 * body for the full `FoodEditor` while keeping the live macro preview on top, so
 * viewing and editing live in one component instead of scattered drawers.
 */
export interface FoodDetailPanelProps {
  food: PassioFood;
  /** Optional override title (defaults to the food name). */
  title?: string;
  className?: string;
  /** Server-provided concern detail (name/description) to enrich the codes. */
  concernDetails?: ConcernDetail[];

  // ----- optional edit mode -----
  /** When provided, the panel becomes editable (shows an Edit/Done toggle). */
  onChange?: (next: PassioFood) => void;
  /** Controlled editing flag (otherwise the panel manages its own). */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;

  // ----- optional re-check -----
  /** Re-run the hardcoded concern checks against the (edited) record. */
  onRecheck?: () => void;
  rechecking?: boolean;

  // ----- layout options (when a parent owns the header/summary) -----
  /** Hide the built-in Edit/Done toggle (parent provides one, e.g. a sticky header). */
  hideEditToggle?: boolean;
  /** Render the concern strip as a compact pill only (parent shows the full list). */
  hideConcernList?: boolean;
  /**
   * Optional "Linked label" UI (e.g. a <LabelLink/>), rendered under the macro
   * summary in both view and edit modes so a food can show/set its label link.
   */
  labelSlot?: React.ReactNode;
}

const Macro: React.FC<{ label: string; value: number; unit?: string }> = ({
  label,
  value,
  unit = "g",
}) => (
  <div className="flex flex-col items-center rounded-lg bg-gray-50 px-3 py-2">
    <span className="text-base font-semibold text-gray-900">
      {Math.round(value * 10) / 10}
      {unit === "" ? "" : <span className="text-xs font-normal"> {unit}</span>}
    </span>
    <span className="text-[11px] uppercase tracking-wide text-gray-500">
      {label}
    </span>
  </div>
);

const ConcernStrip: React.FC<{
  concerns: number[];
  concernDetails?: ConcernDetail[];
  food: PassioFood;
  /** Compact: single status pill, no itemized list (parent shows it). */
  compact?: boolean;
}> = ({ concerns, concernDetails, food, compact }) => {
  const detailByCode = useMemo(() => {
    const m: Record<number, ConcernDetail> = {};
    (concernDetails || []).forEach((d) => {
      if (typeof d.code === "number") m[d.code] = d;
    });
    return m;
  }, [concernDetails]);

  // Prefer a data-specific explanation (e.g. exactly which micro is missing)
  // over the generic catalog text.
  const explain = (code: number): string =>
    explainConcern(code, food) ||
    describeConcern(code, detailByCode[code]).description;

  const views = concerns.map((c) => describeConcern(c, detailByCode[c]));
  const blocking = views.filter((v) => v.blocking);
  const advisory = views.filter((v) => !v.blocking);

  if (concerns.length === 0) {
    // In compact mode (the review card) the parent header already states the
    // item is ready — don't add a second redundant "passes sanity checks" box.
    if (compact) return null;
    return (
      <div className="mt-3 rounded-md border border-green-200 bg-green-50 px-3 py-2">
        <span className="text-xs font-medium text-green-700">
          ✓ No concern flags — passes the sanity checks and is ready to write.
        </span>
      </div>
    );
  }

  // Compact: the parent (e.g. the review card's "Fix before writing" box) owns
  // the blocking list, so don't repeat it here — that just double-stacks the
  // same error bar. Surface only advisory notes (which the parent doesn't show);
  // if there's nothing advisory, render nothing.
  if (compact) {
    if (advisory.length === 0) return null;
    return (
      <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
        <span className="text-xs font-semibold text-amber-800">
          ⚠ {advisory.length} advisory note{advisory.length === 1 ? "" : "s"}
          {blocking.length > 0 ? "" : " — OK to write"}
        </span>
      </div>
    );
  }

  // Blocking present -> red, explain it can't be written + point at fixes.
  if (blocking.length > 0) {
    return (
      <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2">
        <span className="text-xs font-semibold text-red-800">
          ⛔ Can&apos;t write yet — {blocking.length} issue
          {blocking.length === 1 ? "" : "s"} to fix
        </span>
        <p className="mt-1 text-[11px] text-red-700">
          Edit the highlighted fields below, then Save — we re-check
          automatically.
        </p>
        <ul className="mt-1.5 space-y-1">
          {blocking.map((v) => (
            <li key={v.code} className="text-xs leading-snug">
              <span className="font-semibold text-red-700">⛔ {v.name}</span>
              {explain(v.code) ? (
                <span className="text-red-700/90"> — {explain(v.code)}</span>
              ) : null}
            </li>
          ))}
        </ul>
        {advisory.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-[11px] font-semibold text-gray-500">
              {advisory.length} advisory note
              {advisory.length === 1 ? "" : "s"} (won&apos;t block)
            </summary>
            <ul className="mt-1 space-y-1">
              {advisory.map((v) => (
                <li key={v.code} className="text-xs leading-snug text-gray-500">
                  ⚠ {v.name}
                  {explain(v.code) ? ` — ${explain(v.code)}` : ""}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    );
  }

  // Only advisory concerns -> amber, but clearly "still OK to write".
  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
      <span className="text-xs font-semibold text-amber-800">
        ⚠ {advisory.length} advisory note{advisory.length === 1 ? "" : "s"} — OK
        to write
      </span>
      <ul className="mt-1.5 space-y-1">
        {advisory.map((v) => (
          <li key={v.code} className="text-xs leading-snug">
            <span className="font-semibold text-amber-800">⚠ {v.name}</span>
            {explain(v.code) ? (
              <span className="text-amber-700"> — {explain(v.code)}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const FoodDetailPanel: React.FC<FoodDetailPanelProps> = ({
  food,
  title,
  className = "",
  concernDetails,
  onChange,
  editing: editingProp,
  onEditingChange,
  onRecheck,
  rechecking,
  hideEditToggle,
  hideConcernList,
  labelSlot,
}) => {
  const portions: PassioPortion[] = useMemo(
    () => (Array.isArray(food?.portions) ? food.portions : []),
    [food]
  );

  const [portionIndex, setPortionIndex] = useState(() =>
    defaultPortionIndex(portions)
  );
  const selectedPortion = portions[Math.min(portionIndex, portions.length - 1)];
  const [quantity, setQuantity] = useState(() =>
    getEffectiveQuantity(portions[defaultPortionIndex(portions)])
  );

  const [concernDrawerOpen, setConcernDrawerOpen] = useState(false);

  // Editing can be controlled (parent owns it) or self-managed.
  const [editingSelf, setEditingSelf] = useState(false);
  const editing = editingProp ?? editingSelf;
  const editable = !!onChange;
  const setEditing = (v: boolean) => {
    setEditingSelf(v);
    onEditingChange?.(v);
  };

  const grams = selectedPortion
    ? getTotalGrams(selectedPortion, quantity)
    : 100; // no portions -> show raw per-100g values
  const scaled = useMemo(() => scaleNutrition(food, grams), [food, grams]);

  const branded = food.branded || {};
  const tags = Array.isArray(food.tags) ? food.tags : [];
  const concerns = Array.isArray(food.concerns) ? food.concerns : [];
  const webLinks = Array.isArray(food.webLinks) ? food.webLinks : [];
  const origins = Array.isArray(food.origin) ? food.origin : [];
  const productCode = branded.productCode || branded.upc || "";
  const hasNutrients = scaled.nutrients.length > 0;

  const selectPortion = (i: number) => {
    setPortionIndex(i);
    setQuantity(getEffectiveQuantity(portions[i]));
  };

  return (
    <div className={`text-sm text-gray-800 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 border-b border-gray-200 pb-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold leading-tight text-gray-900">
            {title || food.name || food.brandName || "Unnamed product"}
          </h3>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
            {(food.brandName || branded.owner) && (
              <span>by {food.brandName || branded.owner}</span>
            )}
            {branded.country && (
              <Badge tone="gray">{branded.country.replace("en:", "")}</Badge>
            )}
            {productCode ? (
              <span className="font-mono text-gray-600">UPC {productCode}</span>
            ) : (
              <Badge tone="amber">⚠ no UPC — add in editor</Badge>
            )}
          </div>
          {/* Missing-info web lookups (UPC / nutrition) — one-tap Google search. */}
          <WebLookup food={food} className="mt-2" />
        </div>
        {editable && !hideEditToggle && (
          <button
            type="button"
            onClick={() => setEditing(!editing)}
            className={`flex-shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold transition ${
              editing
                ? "border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700"
                : "border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50"
            }`}
          >
            {editing ? "✓ Done" : "✎ Edit"}
          </button>
        )}
      </div>

      {/* Concern strip (read-only; concerns are auto-applied by sanity checks) */}
      <ConcernStrip
        concerns={concerns}
        concernDetails={concernDetails}
        food={food}
        compact={hideConcernList}
      />

      {/* Macro summary — always visible (live preview while editing) */}
      {hasNutrients ? (
        <div className="mt-3">
          <div className="text-xs font-medium text-gray-500">
            Nutrition per{" "}
            {selectedPortion
              ? `${Math.round(quantity * 100) / 100} ${
                  selectedPortion.name
                } (${formatGrams(grams)})`
              : "100 g"}
          </div>
          <div className="mt-2 grid grid-cols-5 gap-2">
            <Macro label="Cals" value={scaled.calories} unit="" />
            <Macro label="Protein" value={scaled.protein} />
            <Macro label="Carbs" value={scaled.carbs} />
            <Macro label="Fat" value={scaled.fat} />
            <Macro label="Fiber" value={scaled.fiber} />
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-xs text-gray-500">
          No nutrient data on this record.
        </div>
      )}

      {/* Linked label (canonical category link that makes this findable in search) */}
      {labelSlot && <div className="mt-4">{labelSlot}</div>}

      {/* Portion picker + quantity (view mode only; edit portions in the editor) */}
      {!editing && portions.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">Portions</span>
            <span className="text-[11px] text-gray-400">
              (pick a serving, then set how many)
            </span>
          </div>
          <div className="mt-2 flex items-start gap-3">
            <div className="flex flex-1 gap-2 overflow-x-auto pb-1">
              {portions.map((p, i) => {
                const isSel = i === portionIndex;
                return (
                  <button
                    key={`${p.name}-${i}`}
                    type="button"
                    onClick={() => selectPortion(i)}
                    className={`flex min-w-[96px] flex-shrink-0 flex-col items-center rounded-lg border px-3 py-2 text-center transition ${
                      isSel
                        ? "border-indigo-500 bg-indigo-50 shadow-sm"
                        : "border-gray-200 bg-white hover:bg-gray-50"
                    }`}
                  >
                    <span className="text-xs font-medium text-gray-600">
                      {p.name}
                    </span>
                    <span className="mt-0.5 text-sm font-semibold text-gray-900">
                      {Math.round((p.weight?.value || 0) * 100) / 100}{" "}
                      {p.weight?.unit}
                    </span>
                    {/* Show the multiplied total on the selected card so it's
                        obvious you're measuring qty × portion, not 1. */}
                    {isSel && quantity !== 1 && (
                      <span className="mt-1 rounded bg-indigo-100 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700">
                        × {Math.round(quantity * 100) / 100} ={" "}
                        {formatGrams(grams)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-shrink-0 flex-col items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50/40 px-3 py-2">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-indigo-600">
                Qty ×
              </label>
              <input
                type="number"
                min={0}
                step={
                  Number.isInteger(getEffectiveQuantity(selectedPortion))
                    ? 1
                    : 0.1
                }
                value={quantity}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  if (!Number.isNaN(v) && v >= 0) setQuantity(v);
                }}
                className="w-20 rounded-md border border-gray-300 px-2 py-1 text-center text-sm font-semibold focus:border-indigo-500 focus:outline-none"
              />
              {selectedPortion && (
                <span className="text-[11px] font-medium text-gray-500">
                  = {formatGrams(grams)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ----- EDIT MODE: full field editor ----- */}
      {editing && onChange && (
        <div className="mt-4">
          <FoodEditor
            value={food}
            onChange={onChange}
            highlightFields={blockingFieldTargets(concerns)}
          />
          {onRecheck && (
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              {rechecking && <Spinner label="re-checking" />}
              <span>
                Click <span className="font-medium text-gray-700">✓ Done</span>{" "}
                when finished — we automatically re-run the sanity checks and
                re-apply the concern codes against your edits.
              </span>
            </div>
          )}
        </div>
      )}

      {/* ----- VIEW MODE: read-only detail ----- */}
      {!editing && (
        <>
          {/* All nutrients */}
          {hasNutrients && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-500">
                All nutrients ({scaled.nutrients.length})
              </div>
              <div className="mt-2 max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 p-2">
                {scaled.nutrients.map((n, i) => (
                  <div
                    key={`${n.name}-${i}`}
                    className="flex items-center justify-between border-b border-gray-200 py-1 text-xs last:border-b-0"
                  >
                    <span className="text-gray-700">
                      {prettyNutrientName(n.name)}
                    </span>
                    <span className="font-medium text-gray-500">
                      {formatAmount(n.amount, n.unit)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product / branded info */}
          {(branded.owner ||
            productCode ||
            branded.country ||
            branded.ingredients) && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-500">
                Product information
              </div>
              <div className="mt-2 space-y-2 rounded-md border border-sky-100 bg-sky-50/60 p-3 text-xs">
                <div className="grid grid-cols-2 gap-2">
                  {branded.owner && (
                    <div>
                      <div className="font-semibold uppercase tracking-wide text-sky-700">
                        Brand
                      </div>
                      <div className="text-sky-900">{branded.owner}</div>
                    </div>
                  )}
                  {productCode && (
                    <div>
                      <div className="font-semibold uppercase tracking-wide text-sky-700">
                        Product code (UPC)
                      </div>
                      <div className="font-mono text-sky-900">
                        {productCode}
                      </div>
                    </div>
                  )}
                  {branded.country && (
                    <div>
                      <div className="font-semibold uppercase tracking-wide text-sky-700">
                        Country
                      </div>
                      <div className="text-sky-900">
                        {branded.country.replace("en:", "")}
                      </div>
                    </div>
                  )}
                </div>
                {branded.ingredients && (
                  <div>
                    <div className="font-semibold uppercase tracking-wide text-sky-700">
                      Ingredients
                    </div>
                    <div className="mt-1 rounded-md bg-white/70 p-2 leading-relaxed text-sky-900">
                      {branded.ingredients}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Concern flags — the applied concern codes with name/description,
              plus a button into the full searchable code reference. */}
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-gray-500">
                Concern flags
                {concerns.length > 0 ? ` (${concerns.length})` : ""}
              </div>
              <button
                type="button"
                onClick={() => setConcernDrawerOpen(true)}
                className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
                title="Open the full concern-code reference (searchable)"
              >
                ⓘ All concern codes
              </button>
            </div>
            {concerns.length === 0 ? (
              <p className="mt-1 text-xs text-gray-400">
                No concern flags on this item.
              </p>
            ) : (
              <div className="mt-2 space-y-1.5">
                {concerns.map((code) => {
                  const v = describeConcern(code);
                  const why = explainConcern(code, food);
                  const isBlock = BLOCKING_CONCERN_CODES.has(code);
                  return (
                    <div
                      key={code}
                      className={`rounded-md border px-2.5 py-1.5 text-xs ${
                        isBlock
                          ? "border-red-200 bg-red-50"
                          : "border-amber-200 bg-amber-50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={`font-semibold ${
                            isBlock ? "text-red-800" : "text-amber-800"
                          }`}
                        >
                          {isBlock ? "⛔" : "⚠"} {v.name}
                        </span>
                        <span className="flex-shrink-0 font-mono text-[10px] text-gray-400">
                          {code}
                        </span>
                      </div>
                      {(why || v.description) && (
                        <p
                          className={`mt-0.5 leading-snug ${
                            isBlock ? "text-red-700/90" : "text-amber-700"
                          }`}
                        >
                          {why || v.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-500">Tags</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Web links */}
          {webLinks.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-500">Web links</div>
              <ul className="mt-1 space-y-0.5">
                {webLinks.map((l, i) => (
                  <li key={i} className="truncate text-xs">
                    <a
                      href={l}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Sources / origins */}
          {origins.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-medium text-gray-500">
                Data sources
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {origins.map((o, i) => (
                  <span
                    key={i}
                    className="rounded-md bg-green-50 px-2 py-0.5 text-xs text-green-800"
                  >
                    {o.source}
                    {o.id ? ` · ${o.id}` : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <ConcernCatalogDrawer
        open={concernDrawerOpen}
        onClose={() => setConcernDrawerOpen(false)}
        activeCodes={concerns}
      />
    </div>
  );
};

export default FoodDetailPanel;
